import { DOMAINS, DOMAIN_FALLBACK, INTENT_IDS, domainOf, type DomainId, type IntentId } from './taxonomy';

/** A probability for every intent and every topic, in the order of `INTENT_IDS` and `DOMAINS`. */
export type Probs = { intent: ArrayLike<number>; domain: ArrayLike<number> };

export type DecisionParams = {
  /** The best intent must reach this probability to be answered as itself. */
  intent: number;
  /** ...and lead the second best by at least this much. */
  margin: number;
  /** Without a clear intent, the topic must reach this to get its general answer. */
  domain: number;
};

export type Decision = {
  intent: IntentId;
  domain: DomainId;
  confidence: number;
  /** `intent`: the exact situation; `domain`: only the topic is clear; `unknown`: nothing is. */
  via: 'intent' | 'domain' | 'unknown';
  runnerUp?: IntentId;
  /** The intent the model liked best and how much, whatever was decided. */
  top: IntentId;
  best: number;
};

function ranked(values: ArrayLike<number>): number[] {
  return Array.from({ length: values.length }, (_, at) => at).toSorted((a, b) => (values[b] ?? 0) - (values[a] ?? 0));
}

/** What the policy needs from a set of probabilities, worked out once so thresholds can be tried cheaply. */
export type Reading = {
  top: IntentId;
  best: number;
  lead: number;
  runnerUp?: IntentId;
  topic: DomainId;
  topicSure: number;
};

export function readProbs(probs: Probs): Reading {
  const [first = 0, second = 0] = ranked(probs.intent);
  const topicAt = ranked(probs.domain)[0] ?? 0;
  const best = probs.intent[first] ?? 0;
  return {
    top: INTENT_IDS[first] ?? 'fuera',
    best,
    lead: best - (probs.intent[second] ?? 0),
    runnerUp: INTENT_IDS[second],
    topic: DOMAINS[topicAt] ?? 'fuera',
    topicSure: probs.domain[topicAt] ?? 0,
  };
}

export function decideFrom({ top, best, lead, runnerUp, topic, topicSure }: Reading, params: DecisionParams): Decision {
  if (best >= params.intent && lead >= params.margin && domainOf(top) === topic) {
    return { intent: top, domain: topic, confidence: best, via: 'intent', runnerUp, top, best };
  }
  if (topicSure >= params.domain) {
    return { intent: DOMAIN_FALLBACK[topic], domain: topic, confidence: topicSure, via: 'domain', runnerUp: top, top, best };
  }
  return { intent: 'fuera', domain: 'fuera', confidence: best, via: 'unknown', runnerUp: top, top, best };
}

/**
 * Turns the model's probabilities into one decision. An exact answer needs a sure intent that
 * agrees with the topic; otherwise a sure topic gets its general answer; otherwise the team says
 * it does not know. That order is what lets a question nobody planned for land somewhere useful.
 */
export function decide(probs: Probs, params: DecisionParams): Decision {
  return decideFrom(readProbs(probs), params);
}
